// v2 cockpit — « Production » (owner, 2026-10-22; v1 « Pipeline » 2026-10-21):
// the 6 steps in one strip, each opening the orders list filtered to its
// statuses. Count, a red « N bloquées » chip when orders sit in the step for
// more than 48 h (orders.status_changed_at, migration 262 — fallback before
// it), and a small track: the step's share of the busiest step. The first 5
// count ACTIVE orders only (activity within 14 days); older ones are in
// Rapports › Dossiers anciens (the step's tooltip says how many).
import { Link } from 'react-router-dom';
import type { PipelineStep } from '@/lib/dashboardApi';
import { pipelineHref, periodTag, type HomePeriod } from '@/lib/homeDashboard';
import { useT } from '@/lib/i18n';
import { HomeCard, HomeCardTitle, HEADING_FONT } from './homeParts';

export function PipelineSteps({ steps, period }: { steps: PipelineStep[]; period: HomePeriod }) {
  const { t } = useT();
  const max = Math.max(1, ...steps.map((s) => s.count));
  return (
    <div className="grid grid-cols-3 lg:grid-cols-6 gap-y-2">
      {steps.map((s, i) => {
        const done = s.key === 'delivered';
        const label = done ? `${t(s.label)} · ${t(periodTag(period))}` : t(s.label);
        const tip = [
          t(s.sub),
          !done && s.over48h > 0 ? t('{n} commandes dans cette étape depuis plus de 48 h', { n: s.over48h }) : null,
          !done && s.old > 0 ? t('+{n} sans activité depuis 14 jours (Rapports → Dossiers anciens)', { n: s.old }) : null,
        ].filter(Boolean).join(' · ');
        return (
          <Link key={s.key} to={pipelineHref(s.statuses)} title={tip} data-step={s.key}
            className={`min-w-0 py-1.5 pr-2 lg:pr-3 rounded-md hover:bg-adm-gold-wash transition-colors ${
              i % 3 === 0 ? 'pl-0' : 'pl-2'} lg:pl-3 ${i === 0 ? 'lg:pl-0' : 'lg:border-l lg:border-[#ECE5D6] dark:lg:border-adm-line'}`}>
            <span className="block text-[11.5px] text-adm-muted font-medium truncate">{label}</span>
            <b className="flex items-center gap-2 text-[22px] leading-tight font-medium text-adm-ink" style={{ fontFamily: HEADING_FONT }}>
              {s.count}
              {!done && s.over48h > 0 && (
                <span className="font-sans text-[10.5px] font-bold text-status-stuck bg-status-stuck-bg rounded-full px-1.5 py-px whitespace-nowrap">
                  {t('{n} bloquées', { n: s.over48h })}
                </span>
              )}
            </b>
            <span aria-hidden className="block h-[5px] rounded-full mt-[5px] overflow-hidden bg-adm-surface-3">
              <i className={`block h-full ${done ? 'bg-status-done-solid' : 'bg-adm-gold'}`} style={{ width: `${Math.round((s.count / max) * 100)}%` }} />
            </span>
          </Link>
        );
      })}
    </div>
  );
}

export function HomePipelineCard({ steps, isLoading, period }: { steps?: PipelineStep[]; isLoading: boolean; period: HomePeriod }) {
  const { t } = useT();
  return (
    <HomeCard id="home-pipeline">
      <HomeCardTitle label={t('Production')} sub={<span className="hidden sm:inline">{t('cliquez une étape pour voir les commandes')}</span>} />
      {isLoading || !steps ? (
        <div className="h-[58px] rounded-[10px] bg-adm-surface-3 animate-pulse" aria-busy />
      ) : (
        <PipelineSteps steps={steps} period={period} />
      )}
    </HomeCard>
  );
}
